import { cn } from '@/lib/utils';
import { ALL_SUB_CATEGORY_ID } from '@/hooks/useAppSearchParams';
import type { SubCategoryResponseDto } from '@/lib/models';

export { ALL_SUB_CATEGORY_ID };

const TEST_ID_PREFIX = 'sub-category-tabs';

export interface SubCategoryTabsProps {
  /** Sub-categories to render after the leading "All" chip. */
  subCategories: SubCategoryResponseDto[];
  /**
   * Currently selected sub-category id. Use {@link ALL_SUB_CATEGORY_ID}
   * (or `""`) to highlight the "All" chip.
   */
  activeSubCategory: string;
  /** Fired when a chip is clicked. */
  onSubCategoryChange?: (subCategoryId: string) => void;
  className?: string;
}

/**
 * Horizontally-scrollable sub-category chip row shown between the category
 * banner and the feed (hosted by {@link SubCategoryBar}). Each chip is a
 * white pill — purple border + purple text when active, soft border + muted
 * text otherwise (Figma 6409-160431 / 6492-186611). Always prepends an
 * "All" chip.
 *
 * @example
 * ```tsx
 * <SubCategoryTabs
 *   subCategories={category.subCategories}
 *   activeSubCategory={activeSubCategory}
 *   onSubCategoryChange={setSubCategory}
 * />
 * ```
 */
export function SubCategoryTabs({ subCategories, activeSubCategory, onSubCategoryChange, className }: SubCategoryTabsProps) {
  const normalisedActive = activeSubCategory || ALL_SUB_CATEGORY_ID;

  const tabs: { id: string; name: string; code: string }[] = [
    { id: ALL_SUB_CATEGORY_ID, name: 'All', code: 'ALL' },
    ...subCategories.map((sc) => ({ id: sc.id, name: sc.displayName || sc.code, code: sc.code })),
  ];

  return (
    <div className={cn('w-full', className)}>
      <div data-testid={TEST_ID_PREFIX} className="flex items-center gap-2 overflow-x-auto px-3.5 md:px-0 scrollbar-hide">
        {tabs.map((tab) => {
          const isActive = tab.code === normalisedActive;
          return (
            <button
              key={tab.id}
              type="button"
              data-testid={`${TEST_ID_PREFIX}-tab-${tab.code.toLowerCase()}`}
              aria-pressed={isActive}
              onClick={() => onSubCategoryChange?.(tab.code)}
              className={cn(
                'flex h-7 min-w-[90px] flex-shrink-0 cursor-pointer items-center justify-center rounded-pill bg-surface px-2 font-sans text-base capitalize tracking-[-0.02em] whitespace-nowrap transition-colors',
                isActive
                  ? 'border-[1.5px] border-purple font-semibold text-purple'
                  : 'border border-stroke-soft font-normal text-ink-muted hover:border-purple/40 hover:text-purple',
              )}
            >
              {tab.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
